/**
 * FDP Owl Lightbox (with a11y)
 *
 * Replaces PhotoSwipe with an Owl Carousel–powered fullscreen lightbox
 * for the Font Detail Page carousel. Clicking any poster image inside
 * .fdp_carousel opens a dark overlay with high-res versions of all
 * posters in a single-item Owl Carousel, starting at the correct index
 * even when the main carousel has clone slides.
 *
 * Accessibility:
 *  - Each carousel image is wrapped in a <button> for keyboard support.
 *  - Buttons announce themselves to screen readers with descriptive labels.
 *  - Visible focus ring for keyboard users.
 *  - Escape key closes the lightbox; arrow keys navigate.
 *  - Focus is trapped inside the overlay while open.
 *  - Respects prefers-reduced-motion.
 *
 * Purely additive — does not modify the main Owl Carousel instance.
 *
 * LOCALIZATION:
 *  All user-visible English strings are grouped in the LABELS object.
 */
(function () {
  "use strict";

  var LABELS = {
    openButtonLabel: "View {altText} enlarged",
    close: "Close lightbox",
    previous: "Previous image",
    next: "Next image",
  };

  if (document.readyState === "complete") {
    setTimeout(initOwlLightbox, 100);
  } else {
    window.addEventListener("load", function () {
      setTimeout(initOwlLightbox, 100);
    });
  }

  function initOwlLightbox() {
    var carouselContainer = document.querySelector(".fdp_carousel .owl-carousel");
    if (!carouselContainer) return;

    if (typeof jQuery === "undefined" || !jQuery.fn.owlCarousel) {
      console.warn("[fdp-owl-lightbox] Owl Carousel not loaded.");
      return;
    }

    var originalSlides = carouselContainer.querySelectorAll(
      ".owl-item:not(.cloned) img"
    );
    if (!originalSlides.length) return;

    var dataSource = [];
    originalSlides.forEach(function (img) {
      var url = img.getAttribute("src");
      var hiResUrl = url.replace(
        /width=\d+,height=\d+,fit=contain/,
        "width=1600,fit=contain"
      );
      dataSource.push({
        src: hiResUrl,
        alt: img.getAttribute("alt") || "",
      });
    });

    injectStyles();
    buildOverlayDOM();

    var allImages = carouselContainer.querySelectorAll(".owl-item img");
    allImages.forEach(function (img) {
      wrapInButton(img);
    });

    setupDragSuppression(carouselContainer);

    var prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    var overlay = document.getElementById("fdp-owl-lightbox-overlay");
    var lightboxCarouselEl = document.getElementById("fdp-owl-lightbox-carousel");
    var closeBtn = document.getElementById("fdp-owl-lightbox-close");
    var counterEl = document.getElementById("fdp-owl-lightbox-counter");
    var owlInstance = null;
    var previouslyFocused = null;

    function updateCounter(index) {
      if (!counterEl || dataSource.length < 2) {
        if (counterEl) counterEl.textContent = "";
        return;
      }
      counterEl.textContent = (index + 1) + " / " + dataSource.length;
    }

    function openLightbox(startIndex) {
      previouslyFocused = document.activeElement;
      document.body.style.overflow = "hidden";
      overlay.classList.add("fdp-owl-lightbox--open");
      overlay.setAttribute("aria-hidden", "false");

      if (owlInstance) {
        owlInstance.trigger("destroy.owl.carousel");
        lightboxCarouselEl.innerHTML = "";
        lightboxCarouselEl.className = "owl-carousel owl-theme fdp-owl-lightbox__carousel";
      }

      dataSource.forEach(function (item) {
        var slide = document.createElement("div");
        slide.className = "fdp-owl-lightbox__slide";
        var img = document.createElement("img");
        img.src = item.src;
        img.alt = item.alt;
        img.draggable = false;
        slide.appendChild(img);
        lightboxCarouselEl.appendChild(slide);
      });

      owlInstance = jQuery(lightboxCarouselEl).owlCarousel({
        items: 1,
        nav: true,
        dots: false,
        loop: dataSource.length > 1,
        startPosition: startIndex,
        mouseDrag: true,
        touchDrag: true,
        smartSpeed: prefersReducedMotion ? 0 : 300,
        navText: [
          '<img src="https://cdn.shopify.com/s/files/1/0584/9628/6901/files/left-arrow.png" alt="' + LABELS.previous + '" width="16" height="23">',
          '<img src="https://cdn.shopify.com/s/files/1/0584/9628/6901/files/right-arrow.png" alt="' + LABELS.next + '" width="16" height="23">',
        ],
      });

      updateCounter(startIndex);
      owlInstance.on("changed.owl.carousel", function (e) {
        var idx = e.item.index;
        if (dataSource.length > 1) {
          idx = (idx - e.relatedTarget._clones.length / 2) % dataSource.length;
          if (idx < 0) idx += dataSource.length;
        }
        updateCounter(idx);
      });

      setTimeout(function () {
        closeBtn.focus();
      }, 50);
    }

    function closeLightbox() {
      overlay.classList.remove("fdp-owl-lightbox--open");
      overlay.setAttribute("aria-hidden", "true");
      document.body.style.overflow = "";

      if (owlInstance) {
        owlInstance.trigger("destroy.owl.carousel");
        lightboxCarouselEl.innerHTML = "";
        lightboxCarouselEl.className = "owl-carousel owl-theme fdp-owl-lightbox__carousel";
        owlInstance = null;
      }

      if (previouslyFocused) {
        previouslyFocused.focus();
        previouslyFocused = null;
      }
    }

    closeBtn.addEventListener("click", closeLightbox);

    overlay.addEventListener("click", function (e) {
      if (e.target === overlay) closeLightbox();
    });

    document.addEventListener("keydown", function (e) {
      if (!overlay.classList.contains("fdp-owl-lightbox--open")) return;

      if (e.key === "Escape") {
        e.preventDefault();
        closeLightbox();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        if (owlInstance) owlInstance.trigger("prev.owl.carousel");
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        if (owlInstance) owlInstance.trigger("next.owl.carousel");
      }
    });

    function handleButtonActivate(event) {
      if (carouselContainer._fdpDragJustHappened) {
        carouselContainer._fdpDragJustHappened = false;
        return;
      }

      var button = event.currentTarget;
      var img = button.querySelector("img");
      if (!img) return;

      var alt = img.getAttribute("alt") || "";
      var match = alt.match(/(\d+)\s*$/);
      var posterNumber = match ? parseInt(match[1], 10) : NaN;

      var index = isNaN(posterNumber) ? 0 : posterNumber - 1;
      if (index < 0) index = 0;
      if (index >= dataSource.length) index = dataSource.length - 1;

      openLightbox(index);
    }

    carouselContainer
      .querySelectorAll(".fdp-lightbox-trigger")
      .forEach(function (button) {
        button.addEventListener("click", handleButtonActivate);
      });
  }

  function buildOverlayDOM() {
    if (document.getElementById("fdp-owl-lightbox-overlay")) return;

    var overlay = document.createElement("div");
    overlay.id = "fdp-owl-lightbox-overlay";
    overlay.className = "fdp-owl-lightbox";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-label", "Image lightbox");
    overlay.setAttribute("aria-hidden", "true");

    var counter = document.createElement("span");
    counter.id = "fdp-owl-lightbox-counter";
    counter.className = "fdp-owl-lightbox__counter";
    counter.setAttribute("aria-live", "polite");

    var closeBtn = document.createElement("button");
    closeBtn.id = "fdp-owl-lightbox-close";
    closeBtn.type = "button";
    closeBtn.className = "fdp-owl-lightbox__close";
    closeBtn.setAttribute("aria-label", LABELS.close);
    closeBtn.innerHTML = "&times;";

    var carouselEl = document.createElement("div");
    carouselEl.id = "fdp-owl-lightbox-carousel";
    carouselEl.className = "owl-carousel owl-theme fdp-owl-lightbox__carousel";

    overlay.appendChild(counter);
    overlay.appendChild(closeBtn);
    overlay.appendChild(carouselEl);
    document.body.appendChild(overlay);
  }

  function wrapInButton(img) {
    if (img.parentElement && img.parentElement.classList.contains("fdp-lightbox-trigger")) {
      return;
    }
    var alt = img.getAttribute("alt") || "image";
    var button = document.createElement("button");
    button.type = "button";
    button.className = "fdp-lightbox-trigger";
    button.setAttribute(
      "aria-label",
      LABELS.openButtonLabel.replace("{altText}", alt)
    );
    img.parentNode.insertBefore(button, img);
    button.appendChild(img);
  }

  function setupDragSuppression(carouselContainer) {
    var startX = 0;
    var startY = 0;
    var threshold = 6;

    carouselContainer.addEventListener("pointerdown", function (e) {
      startX = e.clientX;
      startY = e.clientY;
      carouselContainer._fdpDragJustHappened = false;
    });

    carouselContainer.addEventListener("pointerup", function (e) {
      var dx = Math.abs(e.clientX - startX);
      var dy = Math.abs(e.clientY - startY);
      if (dx > threshold || dy > threshold) {
        carouselContainer._fdpDragJustHappened = true;
      }
    });
  }

  function injectStyles() {
    if (document.getElementById("fdp-owl-lightbox-styles")) return;
    var css =
      ".fdp-lightbox-trigger {" +
      "  background: none; border: 0; padding: 0; margin: 0;" +
      "  display: block; width: 100%; cursor: zoom-in; border-radius: 4px;" +
      "}" +
      ".fdp-lightbox-trigger:focus { outline: none; }" +
      ".fdp-lightbox-trigger:focus-visible {" +
      "  outline: 3px solid #1a73e8; outline-offset: 2px;" +
      "}" +
      ".fdp-lightbox-trigger img { display: block; }" +

      /* Overlay */
      ".fdp-owl-lightbox {" +
      "  position: fixed; inset: 0; z-index: 99999;" +
      "  background: rgba(0,0,0,0.95);" +
      "  display: flex; align-items: center; justify-content: center;" +
      "  overflow: hidden;" +
      "  opacity: 0; visibility: hidden;" +
      "  transition: opacity .25s ease, visibility .25s ease;" +
      "}" +
      ".fdp-owl-lightbox--open { opacity: 1; visibility: visible; }" +

      /* Counter — matches PhotoSwipe ".pswp__counter" position & style */
      ".fdp-owl-lightbox__counter {" +
      "  position: absolute; top: 15px; left: 20px; z-index: 10;" +
      "  height: 30px; line-height: 30px;" +
      "  font-size: 14px; color: rgba(255,255,255,0.85);" +
      "  text-shadow: 1px 1px 3px rgba(0,0,0,0.6);" +
      "  user-select: none; -webkit-user-select: none;" +
      "  pointer-events: none;" +
      "}" +

      /* Close button */
      ".fdp-owl-lightbox__close {" +
      "  position: absolute; top: 16px; right: 16px; z-index: 10;" +
      "  background: none; border: 0; color: #fff; font-size: 36px;" +
      "  line-height: 1; cursor: pointer; padding: 4px 12px;" +
      "  border-radius: 4px;" +
      "}" +
      ".fdp-owl-lightbox__close:hover { background: rgba(255,255,255,0.15); }" +
      ".fdp-owl-lightbox__close:focus-visible {" +
      "  outline: 2px solid #fff; outline-offset: 2px;" +
      "}" +

      /* Inner carousel container */
      ".fdp-owl-lightbox__carousel {" +
      "  width: 90vw; max-width: 1600px;" +
      "}" +

      /* Slide images */
      ".fdp-owl-lightbox__slide { display: flex; align-items: center; justify-content: center; }" +
      ".fdp-owl-lightbox__slide img {" +
      "  max-width: 100%; max-height: 85vh; object-fit: contain;" +
      "  user-select: none; -webkit-user-select: none;" +
      "}" +

      /* Owl nav inside lightbox — spans full viewport width so arrows
         sit outside the image area; clamp() keeps them on-screen at all sizes. */
      ".fdp-owl-lightbox .owl-nav {" +
      "  position: fixed !important; top: 50% !important;" +
      "  left: 0 !important; right: 0 !important;" +
      "  transform: translateY(-50%) !important;" +
      "  width: 100% !important; max-width: none !important;" +
      "  margin: 0 !important;" +
      "  display: flex !important; justify-content: space-between !important;" +
      "  pointer-events: none;" +
      "  padding: 0 clamp(8px, 3vw, 48px) !important;" +
      "  box-sizing: border-box !important;" +
      "}" +
      ".fdp-owl-lightbox .owl-nav button.owl-prev," +
      ".fdp-owl-lightbox .owl-nav button.owl-next {" +
      "  position: static !important;" +
      "  left: auto !important; right: auto !important;" +
      "  margin: 0 !important;" +
      "  pointer-events: auto; background: rgba(255,255,255,0.12) !important;" +
      "  color: #fff !important; font-size: 0 !important; line-height: 1 !important;" +
      "  width: 52px; height: 52px; border-radius: 50% !important;" +
      "  display: flex !important; align-items: center; justify-content: center;" +
      "  border: 0 !important; cursor: pointer; transition: background .15s;" +
      "  padding: 0 !important;" +
      "}" +
      ".fdp-owl-lightbox .owl-nav button.owl-prev img," +
      ".fdp-owl-lightbox .owl-nav button.owl-next img {" +
      "  width: 12px; height: 16px;" +
      "  filter: brightness(0) invert(1);" +
      "  pointer-events: none;" +
      "}" +
      ".fdp-owl-lightbox .owl-nav button.owl-prev span," +
      ".fdp-owl-lightbox .owl-nav button.owl-next span {" +
      "  display: none !important;" +
      "}" +
      ".fdp-owl-lightbox .owl-nav button.owl-prev::before," +
      ".fdp-owl-lightbox .owl-nav button.owl-next::before {" +
      "  content: none !important;" +
      "}" +
      ".fdp-owl-lightbox .owl-nav button:hover {" +
      "  background: rgba(255,255,255,0.25) !important;" +
      "}" +
      ".fdp-owl-lightbox .owl-nav .disabled { opacity: 0.3; cursor: default; }" +

      /* Tablet: shrink nav buttons, narrow carousel to avoid overlap */
      "@media screen and (max-width: 1024px) {" +
      "  .fdp-owl-lightbox__carousel { width: 80vw !important; }" +
      "  .fdp-owl-lightbox .owl-nav button.owl-prev," +
      "  .fdp-owl-lightbox .owl-nav button.owl-next {" +
      "    width: 40px !important; height: 40px !important;" +
      "  }" +
      "  .fdp-owl-lightbox .owl-nav button.owl-prev img," +
      "  .fdp-owl-lightbox .owl-nav button.owl-next img {" +
      "    width: 10px; height: 14px;" +
      "  }" +
      "}" +
      /* Phone: further shrink */
      "@media screen and (max-width: 767px) {" +
      "  .fdp-owl-lightbox__carousel { width: 75vw !important; }" +
      "  .fdp-owl-lightbox .owl-nav button.owl-prev," +
      "  .fdp-owl-lightbox .owl-nav button.owl-next {" +
      "    width: 36px !important; height: 36px !important;" +
      "  }" +
      "}" +

      /* Reduced motion */
      "@media (prefers-reduced-motion: reduce) {" +
      "  .fdp-owl-lightbox { transition: none; }" +
      "}";

    var style = document.createElement("style");
    style.id = "fdp-owl-lightbox-styles";
    style.appendChild(document.createTextNode(css));
    document.head.appendChild(style);
  }
})();
